import { configSettings } from '@/lib/config';
import { NavigateOptions, useBlocker, useNavigate } from 'react-router-dom';
import { pathToRegexp } from 'path-to-regexp';
import { MIGRATED_ROUTES } from '@/lib/constants';
import { useNavigationStore } from '@/stores/navigationStore';
import { useEffect } from 'react';

export function useStanRouter() {
    const navigate = useNavigate();
    const { isDirty, setBlocker, setShowConfirmation } = useNavigationStore();

    // Use React Router's built-in blocker for navigation blocking
    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        // Only block if:
        // 1. There are unsaved changes (isDirty checks bypassNextCheck internally)
        // 2. We're actually navigating to a different location
        return isDirty() && currentLocation.pathname !== nextLocation.pathname;
    });

    // Handle blocker state changes
    useEffect(() => {
        if (blocker.state === 'blocked') {
            // Store the blocker for later use
            setBlocker(blocker);
            setShowConfirmation(true);
        }
    }, [blocker.state, blocker, setBlocker, setShowConfirmation]);

    const push = (
        path: string,
        options?: NavigateOptions & { skipDirtyCheck?: boolean },
        skipAnimation: boolean = false
    ) => {
        const cleanPath = normalizePath(path);
        if (!cleanPath) {
            console.error('[STAN-ROUTER] - no-path-found');
            return;
        }

        // Set bypass flag if skipDirtyCheck is true
        if (options?.skipDirtyCheck) {
            useNavigationStore.setState({ bypassNextCheck: true });
        }

        // Proceed with navigation - useBlocker will handle dirty check
        executeNavigation(path, options, skipAnimation);
    };

    const executeNavigation = (path: string, options?: NavigateOptions, skipAnimation: boolean = false) => {
        if (isMigratedPath(normalizePath(path)) || configSettings.isDevelopment) {
            if (skipAnimation || !document.startViewTransition) {
                navigate(path, options);
            } else {
                // Use browsers built in page transition effect
                document.startViewTransition(() => {
                    navigate(path, options);
                });
            }
        } else {
            const legacyDomain = configSettings.legacyAdminDomain?.replace(/\/+$/, '');
            const legacyPath = path.startsWith('/') ? path : `/${path}`;
            window.location.href = `${legacyDomain}${legacyPath}`;
        }
    };

    const back = () => {
        const referrer = document.referrer;

        // Fallback to navigate(-1) if no referrer
        if (!referrer) {
            navigate(-1);
            return;
        }

        try {
            const refPath = new URL(referrer).pathname;
            if (isMigratedPath(refPath) || configSettings.isDevelopment) {
                // Previous route is migrated OR we're in local/staging - use React router
                navigate(-1);
            } else {
                // Previous route not migrated and in production - redirect to legacy Vue admin
                window.location.href = referrer;
            }
        } catch (err) {
            console.warn('[STAN-ROUTER] - Failed to parse referrer URL:', err);
            navigate(-1);
        }
    };

    return { push, back };
}

function normalizePath(path: string): string {
    return path.split('?')[0]?.replace(/\/$/, '') || '';
}

function isMigratedPath(path: string): boolean {
    return MIGRATED_ROUTES.some(pattern => {
        // Handle wildcard patterns manually for better matching
        if (pattern.endsWith('/*')) {
            const basePattern = pattern.slice(0, -2); // Remove '/*'
            return path === basePattern || path.startsWith(basePattern + '/');
        }

        // Use pathToRegexp for dynamic routes like :paymentId
        const regexp = pathToRegexp(pattern).regexp;
        return regexp.test(path);
    });
}
